import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSpaces } from '../context/SpaceContext';
import api from '../services/api';
import { searchGlobal } from '../services/searchService';
import { getDMs } from '../services/dmService';
import './Sidebar.css';

const formatGroupLabel = (value, fallback) => {
  const text = String(value || '').trim();
  return text || fallback;
};

const buildSpaceGroups = (spaces) => {
  const organizationMap = new Map();

  for (const space of spaces) {
    const organizationLabel = formatGroupLabel(space.organizationKey, 'Independent');
    const teamLabel = formatGroupLabel(space.teamKey, 'General');
    const classification = String(space.governance?.classification || 'internal');

    if (!organizationMap.has(organizationLabel)) {
      organizationMap.set(organizationLabel, new Map());
    }

    const teamMap = organizationMap.get(organizationLabel);
    if (!teamMap.has(teamLabel)) {
      teamMap.set(teamLabel, []);
    }

    teamMap.get(teamLabel).push({ ...space, classification });
  }

  return Array.from(organizationMap.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([organizationLabel, teamMap]) => ({
      organizationLabel,
      teams: Array.from(teamMap.entries())
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([teamLabel, teamSpaces]) => ({
          teamLabel,
          spaces: teamSpaces.sort((left, right) => left.name.localeCompare(right.name)),
        })),
    }));
};

export default function Sidebar({ isOpen, onToggle, onNavigate }) {
  const { user, logout } = useAuth();
  const { spaces, createSpace } = useSpaces();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNewSpace, setShowNewSpace] = useState(false);
  const [showSpacesList, setShowSpacesList] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 640;
  });
  const [topNavCollapsed, setTopNavCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('comsapp.sidebar.topNavCollapsed') === '1';
  });
  const [topNavHeight, setTopNavHeight] = useState(() => {
    if (typeof window === 'undefined') return 280;
    const saved = Number(window.localStorage.getItem('comsapp.sidebar.topNavHeight'));
    if (!Number.isFinite(saved) || saved <= 0) return 280;
    return saved;
  });
  const [spacesCollapsed, setSpacesCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('comsapp.sidebar.spacesCollapsed') === '1';
  });
  const [isCompactSpaces, setIsCompactSpaces] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 640;
  });
  const [collapsedOrganizations, setCollapsedOrganizations] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('comsapp.sidebar.collapsedOrganizations');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [collapsedTeams, setCollapsedTeams] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('comsapp.sidebar.collapsedTeams');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [newSpaceName, setNewSpaceName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [focusSearchAfterOpen, setFocusSearchAfterOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [dmUnread, setDmUnread] = useState(0);
  const prevInboxUnreadRef = useRef(0);
  const prevDMUnreadRef = useRef(0);
  const baselineSetRef = useRef(false);
  const soundRef = useRef(null);
  const searchInputRef = useRef(null);
  const groupedSpaces = buildSpaceGroups(spaces);

  const formatUnread = (count) => (count > 99 ? '99+' : String(count));

  const playNotificationSound = () => {
    if (!soundRef.current) return;
    soundRef.current.currentTime = 0;
    soundRef.current.play().catch(() => {});
  };

  const notifyBrowser = (title, body) => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    try {
      new Notification(title, { body });
    } catch {
      // Ignore browser notification failures and keep badge updates.
    }
  };

  useEffect(() => {
    soundRef.current = new Audio('/sound/Message.wav');
    soundRef.current.volume = 0.6;
    return () => {
      soundRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('comsapp.sidebar.topNavCollapsed', topNavCollapsed ? '1' : '0');
  }, [topNavCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('comsapp.sidebar.spacesCollapsed', spacesCollapsed ? '1' : '0');
  }, [spacesCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('comsapp.sidebar.collapsedOrganizations', JSON.stringify(collapsedOrganizations));
  }, [collapsedOrganizations]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('comsapp.sidebar.collapsedTeams', JSON.stringify(collapsedTeams));
  }, [collapsedTeams]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('comsapp.sidebar.topNavHeight', String(topNavHeight));
  }, [topNavHeight]);

  useEffect(() => {
    if (!isOpen || !focusSearchAfterOpen || !searchInputRef.current) return;
    searchInputRef.current.focus();
    setFocusSearchAfterOpen(false);
  }, [isOpen, focusSearchAfterOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError('');
      setSearchLoading(false);
      return undefined;
    }

    let isCancelled = false;
    setSearchLoading(true);

    const timerId = window.setTimeout(() => {
      searchGlobal(query, 7)
        .then((res) => {
          if (isCancelled) return;
          setSearchResults(res.data.results || []);
          setSearchError('');
        })
        .catch((err) => {
          if (isCancelled) return;
          setSearchResults([]);
          setSearchError(err?.response?.data?.message || 'Search failed.');
        })
        .finally(() => {
          if (!isCancelled) setSearchLoading(false);
        });
    }, 250);

    return () => {
      isCancelled = true;
      window.clearTimeout(timerId);
    };
  }, [isOpen, searchQuery]);

  useEffect(() => {
    const clampTopNavHeight = (value) => {
      const min = 120;
      const max = Math.max(220, Math.floor(window.innerHeight * 0.58));
      return Math.max(min, Math.min(max, value));
    };

    const onResize = () => {
      const compact = window.innerWidth <= 640;
      setIsCompactSpaces(compact);
      if (!compact) {
        setShowSpacesList(true);
      }
      setTopNavHeight((value) => clampTopNavHeight(value));
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadUnreadCounts = async () => {
      try {
        const [inboxRes, dmsRes] = await Promise.all([
          api.get('/inbox/unread-count'),
          getDMs(),
        ]);

        const nextInboxUnread = Number(inboxRes?.data?.count || 0);
        const threads = Array.isArray(dmsRes?.data?.threads) ? dmsRes.data.threads : [];
        const nextDMUnread = threads.reduce((sum, thread) => sum + Number(thread?.unreadCount || 0), 0);

        if (!isMounted) return;

        setInboxUnread(nextInboxUnread);
        setDmUnread(nextDMUnread);

        if (baselineSetRef.current) {
          let shouldPlaySound = false;

          if (nextInboxUnread > prevInboxUnreadRef.current && location.pathname !== '/inbox') {
            notifyBrowser('New Email', 'You have new unread inbox messages.');
            shouldPlaySound = true;
          }

          if (nextDMUnread > prevDMUnreadRef.current && location.pathname !== '/messages') {
            notifyBrowser('New Message', 'You have new unread direct messages.');
            shouldPlaySound = true;
          }

          if (shouldPlaySound) {
            playNotificationSound();
          }
        }

        prevInboxUnreadRef.current = nextInboxUnread;
        prevDMUnreadRef.current = nextDMUnread;
        baselineSetRef.current = true;
      } catch {
        if (!isMounted) return;
        setInboxUnread((current) => current);
        setDmUnread((current) => current);
      }
    };

    loadUnreadCounts();
    const intervalId = setInterval(loadUnreadCounts, 12000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [location.pathname]);

  const handleCreateSpace = async (e) => {
    e.preventDefault();
    if (!newSpaceName.trim()) return;
    try {
      const space = await createSpace({ name: newSpaceName.trim() });
      setNewSpaceName('');
      setShowNewSpace(false);
      navigate(`/spaces/${space._id}`);
    } catch (err) {
      console.error('Failed to create space:', err);
      alert(err?.response?.data?.message || 'Failed to create space');
    }
  };

  const handleNavSelect = () => {
    if (typeof onNavigate === 'function') onNavigate();
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchError('');
  };

  const handleSearchSelect = (result) => {
    clearSearch();
    navigate(result.href || '/');
    handleNavSelect();
  };

  const orgKeyFor = (organizationLabel) => organizationLabel;
  const teamKeyFor = (organizationLabel, teamLabel) => `${organizationLabel}::${teamLabel}`;

  const toggleOrganization = (organizationLabel) => {
    const key = orgKeyFor(organizationLabel);
    setCollapsedOrganizations((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTeam = (organizationLabel, teamLabel) => {
    const key = teamKeyFor(organizationLabel, teamLabel);
    setCollapsedTeams((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clampTopNavHeight = (value) => {
    if (typeof window === 'undefined') return value;
    const min = 120;
    const max = Math.max(220, Math.floor(window.innerHeight * 0.58));
    return Math.max(min, Math.min(max, value));
  };

  const beginTopNavResize = (event) => {
    if (!isOpen || hideTopNav || spacesCollapsed) return;
    if (typeof event.button === 'number' && event.button !== 0) return;

    event.preventDefault();

    const startY = event.clientY;
    const startHeight = topNavHeight;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientY - startY;
      setTopNavHeight(clampTopNavHeight(startHeight + delta));
    };

    const onUp = () => {
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleSplitterKeyDown = (event) => {
    if (!isOpen || hideTopNav || spacesCollapsed) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    event.preventDefault();
    const delta = event.key === 'ArrowUp' ? -20 : 20;
    setTopNavHeight((value) => clampTopNavHeight(value + delta));
  };

  const hideTopNav = isOpen && topNavCollapsed;

  return (
    <aside className={`sidebar ${isOpen ? '' : 'collapsed'}`}>
      <div className="sidebar-header">
        <span className="sidebar-logo">ComSapp</span>
        <button className="sidebar-toggle" onClick={onToggle} title="Toggle sidebar">
          {isOpen ? '‹' : '›'}
        </button>
      </div>

      <div
        className={`sidebar-top-nav ${hideTopNav ? 'collapsed' : ''} ${spacesCollapsed ? 'spaces-collapsed' : ''}`}
        style={isOpen && !hideTopNav && !spacesCollapsed ? { height: `${topNavHeight}px` } : undefined}
      >
        {isOpen && (
          <div className="sidebar-section-header sidebar-top-nav-header">
            <span>Menu</span>
            <button
              className="btn-xs-muted"
              type="button"
              onClick={() => setTopNavCollapsed((v) => !v)}
              aria-expanded={!hideTopNav}
              aria-controls="sidebar-top-nav-links"
              title={hideTopNav ? 'Expand top menu' : 'Collapse top menu'}
            >
              {hideTopNav ? 'Expand' : 'Collapse'}
            </button>
          </div>
        )}

        <nav id="sidebar-top-nav-links" className="sidebar-nav" onClick={handleNavSelect}>
          <NavLink to="/" end className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">⊞</span>
            {isOpen && <span>Dashboard</span>}
          </NavLink>
          <NavLink to="/inbox" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon-wrap">
              <span className="nav-icon">✉</span>
              {!isOpen && inboxUnread > 0 && (
                <span className="nav-dot-badge" aria-label={`${inboxUnread} unread inbox messages`}>
                  {formatUnread(inboxUnread)}
                </span>
              )}
            </span>
            {isOpen && <span>Inbox</span>}
            {isOpen && inboxUnread > 0 && <span className="nav-count-badge">{formatUnread(inboxUnread)}</span>}
          </NavLink>
          <NavLink to="/messages" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon-wrap">
              <span className="nav-icon">💬</span>
              {!isOpen && dmUnread > 0 && (
                <span className="nav-dot-badge" aria-label={`${dmUnread} unread direct messages`}>
                  {formatUnread(dmUnread)}
                </span>
              )}
            </span>
            {isOpen && <span>Messages</span>}
            {isOpen && dmUnread > 0 && <span className="nav-count-badge">{formatUnread(dmUnread)}</span>}
          </NavLink>
          <NavLink to="/contacts" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">👥</span>
            {isOpen && <span>Contacts</span>}
          </NavLink>
          <NavLink to="/calendar" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">◷</span>
            {isOpen && <span>Calendar</span>}
          </NavLink>
          <NavLink to="/files" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">⊘</span>
            {isOpen && <span>Files</span>}
          </NavLink>
          <NavLink to="/mailboxes" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">📫</span>
            {isOpen && <span>Mailboxes</span>}
          </NavLink>
          <NavLink to="/clients" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">🏢</span>
            {isOpen && <span>Clients</span>}
          </NavLink>
          <NavLink to="/businesses" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">🏬</span>
            {isOpen && <span>Businesses</span>}
          </NavLink>
          <NavLink to="/projects" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">📁</span>
            {isOpen && <span>Projects</span>}
          </NavLink>
          <NavLink to="/tasks" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">✅</span>
            {isOpen && <span>Tasks</span>}
          </NavLink>
          <NavLink to="/mileage" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">🚗</span>
            {isOpen && <span>Mileage</span>}
          </NavLink>
          <NavLink to="/time" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">⏱</span>
            {isOpen && <span>Time</span>}
          </NavLink>
          <NavLink to="/wrike/supply-requests" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            <span className="nav-icon">📦</span>
            {isOpen && <span>Supply Requests</span>}
          </NavLink>
        </nav>
      </div>

      {isOpen && !hideTopNav && !spacesCollapsed && (
        <div className="sidebar-splitter-wrap">
          <div
            className="sidebar-splitter"
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize menu and spaces"
            tabIndex={0}
            onPointerDown={beginTopNavResize}
            onKeyDown={handleSplitterKeyDown}
          />
        </div>
      )}

      {isOpen && (
        <div className={`sidebar-section ${spacesCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-section-header">
            <span>Spaces</span>
            <div className="sidebar-section-actions">
              {isCompactSpaces && (
                <button
                  className="btn-xs-muted"
                  onClick={() => setShowSpacesList((v) => !v)}
                  title={showSpacesList ? 'Hide spaces' : 'Show spaces'}
                >
                  {showSpacesList ? 'Hide' : `Show (${spaces.length})`}
                </button>
              )}
              <button
                className="btn-xs-muted"
                type="button"
                onClick={() => setSpacesCollapsed((v) => !v)}
                aria-expanded={!spacesCollapsed}
                aria-controls="sidebar-spaces-list"
                title={spacesCollapsed ? 'Expand spaces' : 'Collapse spaces'}
              >
                {spacesCollapsed ? 'Expand' : 'Collapse'}
              </button>
              <button className="btn-icon" onClick={() => setShowNewSpace((v) => !v)} title="New space">+</button>
            </div>
          </div>

          {!spacesCollapsed && showNewSpace && (
            <form onSubmit={handleCreateSpace} className="new-space-form">
              <input
                autoFocus
                value={newSpaceName}
                onChange={(e) => setNewSpaceName(e.target.value)}
                placeholder="Space name..."
                className="sidebar-input"
              />
              <button type="submit" className="btn-sm btn-primary">Create</button>
            </form>
          )}

          {!spacesCollapsed && (!isCompactSpaces || showSpacesList) && (
            <ul id="sidebar-spaces-list" className="space-list" onClick={handleNavSelect}>
              {groupedSpaces.map((group) => (
                <li key={group.organizationLabel} className="space-group-block">
                  <button
                    type="button"
                    className="space-folder-toggle space-group-label"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleOrganization(group.organizationLabel);
                    }}
                    aria-expanded={!collapsedOrganizations[orgKeyFor(group.organizationLabel)]}
                  >
                    <span className={`space-folder-caret ${collapsedOrganizations[orgKeyFor(group.organizationLabel)] ? 'collapsed' : ''}`}>▾</span>
                    <span className="space-folder-name">{group.organizationLabel}</span>
                    <span className="space-folder-count">{group.teams.reduce((sum, team) => sum + team.spaces.length, 0)}</span>
                  </button>

                  {!collapsedOrganizations[orgKeyFor(group.organizationLabel)] && group.teams.map((team) => (
                    <div key={`${group.organizationLabel}-${team.teamLabel}`} className="space-team-block">
                      <button
                        type="button"
                        className="space-folder-toggle space-team-label"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleTeam(group.organizationLabel, team.teamLabel);
                        }}
                        aria-expanded={!collapsedTeams[teamKeyFor(group.organizationLabel, team.teamLabel)]}
                      >
                        <span className={`space-folder-caret ${collapsedTeams[teamKeyFor(group.organizationLabel, team.teamLabel)] ? 'collapsed' : ''}`}>▾</span>
                        <span className="space-folder-name">{team.teamLabel}</span>
                        <span className="space-folder-count">{team.spaces.length}</span>
                      </button>

                      {!collapsedTeams[teamKeyFor(group.organizationLabel, team.teamLabel)] && (
                        <ul className="space-team-list">
                          {team.spaces.map((space) => (
                            <li key={space._id}>
                              <NavLink
                                to={`/spaces/${space._id}`}
                                className={({ isActive }) => isActive ? 'space-item active' : 'space-item'}
                              >
                                <span className={`space-badge classification-${space.classification}`}>{space.name[0].toUpperCase()}</span>
                                <span className="space-name">{space.name}</span>
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="sidebar-footer">
        {isOpen ? (
          <div className="sidebar-search">
            <label className="sidebar-search-label" htmlFor="sidebar-search-input">Search</label>
            <div className="sidebar-search-row">
              <span className="sidebar-search-icon">⌕</span>
              <input
                id="sidebar-search-input"
                ref={searchInputRef}
                className="sidebar-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search spaces, files, people..."
                aria-label="Search everything"
              />
              {searchQuery && (
                <button type="button" className="sidebar-search-clear" onClick={clearSearch} aria-label="Clear search">
                  ×
                </button>
              )}
            </div>
            {searchLoading && <p className="sidebar-search-status">Searching...</p>}
            {searchError && <p className="sidebar-search-status sidebar-search-error">{searchError}</p>}
            {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length > 0 && (
              <ul className="sidebar-search-results" aria-label="Search results">
                {searchResults.map((result) => (
                  <li key={`${result.type}-${result.meta?.id || result.title}`}>
                    <button type="button" className="sidebar-search-result" onClick={() => handleSearchSelect(result)}>
                      <span className={`sidebar-search-pill type-${result.type}`}>{result.type}</span>
                      <span className="sidebar-search-result-text">
                        <strong>{result.title}</strong>
                        <span>{result.subtitle}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length === 0 && !searchError && (
              <p className="sidebar-search-status">No results found.</p>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="sidebar-search-collapsed"
            onClick={() => {
              setFocusSearchAfterOpen(true);
              onToggle?.();
            }}
            title="Open search"
            aria-label="Open search"
          >
            ⌕
          </button>
        )}
        {user?.role === 'admin' && (
          <NavLink
            to="/admin"
            className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
            style={{ margin: '0 0 6px 0' }}
            onClick={handleNavSelect}
          >
            <span className="nav-icon">⚙</span>
            {isOpen && <span>Admin</span>}
          </NavLink>
        )}
        <NavLink
          to="/account"
          className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
          style={{ margin: '0 0 6px 0' }}
          onClick={handleNavSelect}
        >
          <span className="nav-icon">👤</span>
          {isOpen && <span>Account</span>}
        </NavLink>
        <div className="user-info">
          <div className="user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
          {isOpen && (
            <div className="user-details">
              <span className="user-name">{user?.name}</span>
              <button className="btn-logout" onClick={logout}>Sign out</button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
